import { BarChart3, List } from "lucide-react";
import { useLocation } from "react-router-dom";

import { useDealCaptureLookups, useDealCaptureOverview } from "@/hooks/useDealCapture";
import {
  CONTRACT_JUMPS,
  FLOW_PARAM,
  areaLabel,
  areaListPath,
  areaPath,
  keepFlow,
  locate,
} from "@/lib/deal-capture/paths";
import { scrollToSection } from "@/lib/deal-capture/scroll";
import { dealsInFlow, flowInScope } from "@/lib/deal-capture/flow";
import { formatNumber } from "@/lib/format";
import type { SidebarSlotProps } from "@/lib/module-registry";
import { ENTITY_KIND_LABELS } from "@/types/deal-capture";

import { DealJumpTree } from "./DealJumpTree";
import { ExploreRow } from "./ExploreRow";

/**
 * Deal Capture's Explore tree, under the module's Overview row
 * (`ModuleDefinition.sidebarNav`).
 *
 * Reads the area and the record in scope off the url. With no area it says
 * how to start; with one it offers that area's Summary and lists, and with a
 * deal or contract open it lists the page's sections as jump links. Jump links
 * scroll; they never filter or navigate.
 */
export function DealCaptureExplore({ onClose }: SidebarSlotProps) {
  const { pathname, search } = useLocation();
  const { data: overview } = useDealCaptureOverview();
  const { data: lookups } = useDealCaptureLookups();
  const { area, recordId, page } = locate(pathname);

  if (!area) {
    return (
      <p className="px-3 pb-2 text-xs text-text-dim">
        Select an area above to explore deals, contracts, entities or deleted deals.
      </p>
    );
  }

  const count = (n: number | undefined) => (n === undefined ? undefined : formatNumber(n));
  const onSummary = !recordId && page === null;
  // In Deals, counts and links follow the flow in scope: an open deal's own,
  // else the toggle's. `flowSearch` carries it onto links.
  const openDeal = area === "deals" ? lookups?.deals.find((d) => d.number === recordId) : undefined;
  const flow = flowInScope(search, openDeal);
  const flowSearch = flow === "non_integrated" ? `?${FLOW_PARAM}=non_integrated` : "";
  const flowDeals = lookups ? dealsInFlow(lookups.deals, flow) : undefined;
  const summaryCount = area === "deals" ? flowDeals?.length : overview?.counts[area];

  return (
    <div className="mb-2 flex flex-col gap-0.5 pl-4" role="group" aria-label="Deal Capture explore">
      <ExploreRow
        onClose={onClose}
        label={`${areaLabel(area)} Summary`}
        icon={BarChart3}
        count={count(summaryCount)}
        to={keepFlow(areaPath(area), area === "deals" ? flowSearch : "")}
        active={onSummary}
      />
      <div className="my-1 h-px bg-border" />

      {area === "deals" &&
        (() => {
          if (openDeal) {
            return <DealJumpTree key={openDeal.number} deal={openDeal} onClose={onClose} />;
          }
          return (
            <ExploreRow
              onClose={onClose}
              label="All deals"
              icon={List}
              count={count(flowDeals?.length)}
              to={keepFlow(areaListPath("deals"), flowSearch)}
              active={page === "list"}
            />
          );
        })()}

      {area === "contracts" &&
        (() => {
          const contract = lookups?.contracts.find((c) => c.id === recordId);
          if (contract) {
            return (
              <>
                <div className="px-2.5 pb-1 pt-1 text-xs font-bold uppercase tracking-eyebrow text-text-dim">
                  {contract.number}
                </div>
                {CONTRACT_JUMPS.map((jump) => (
                  <ExploreRow
                    onClose={onClose}
                    key={jump.id}
                    label={jump.label}
                    onJump={() => scrollToSection(jump.id)}
                  />
                ))}
              </>
            );
          }
          return (
            <ExploreRow
              onClose={onClose}
              label="All contracts"
              icon={List}
              count={count(lookups?.contracts.length)}
              to={areaListPath("contracts")}
              active={page === "list"}
            />
          );
        })()}

      {area === "entities" &&
        (() => {
          const entity = lookups?.entities.find((e) => e.id === recordId);
          const entities = lookups?.entities ?? [];
          const type = new URLSearchParams(search).get("type");
          return (
            <>
              <ExploreRow
                onClose={onClose}
                label="All entities"
                icon={List}
                count={count(lookups?.entities.length)}
                to={areaListPath("entities")}
                active={page === "list" && !type}
              />
              {(["customer", "trade"] as const).map((kind) => (
                <ExploreRow
                  onClose={onClose}
                  key={kind}
                  label={kind === "customer" ? "Customers" : ENTITY_KIND_LABELS[kind]}
                  count={count(entities.filter((e) => e.kind === kind).length)}
                  to={`${areaListPath("entities")}?type=${kind}`}
                  active={page === "list" && type === kind}
                />
              ))}
              {entity && (
                <div className="mt-2 rounded-md border-l-2 border-l-primary bg-surface-elevated px-2.5 py-2">
                  <div className="text-xs font-bold uppercase tracking-eyebrow text-text-dim">
                    Selected
                  </div>
                  <div className="text-sm font-semibold text-primary">{entity.shortName}</div>
                  <div className="truncate text-xs text-muted-foreground" title={entity.legalName}>
                    {entity.legalName}
                  </div>
                </div>
              )}
            </>
          );
        })()}

      {area === "deleted" && (
        <ExploreRow
          onClose={onClose}
          label="Deleted deals"
          icon={List}
          count={count(overview?.counts.deleted)}
          to={areaListPath("deleted")}
          active={page === "list"}
        />
      )}
    </div>
  );
}
